import CustomHeader from 'components/ReusableComponents/HeaderTitles'
import AnnouncementIcon from 'components/ReusableComponents/Icons/Announcement'
import LoadingIndicator from 'components/ReusableComponents/LoadingIndicator'
import NoMessages from 'components/ReusableComponents/Messaging/NoMessages'
import RefreshIcon from 'components/ReusableComponents/RefreshIcon'
import { useEngagePlusGate } from 'components/ReusableComponents/UnlockServiceDialog'
import AudienceInfoBar from 'features/Announcements/AudienceInfoBar'
import ConversationBubble from 'features/Announcements/ConversationBubble'
import DateDivider from 'features/Announcements/DateDivider'
import EditTextDialog from 'features/Announcements/EditTextDialog'
import SendMessageBar from 'features/Announcements/SendMessageBar'
import { customCampaignStatusTypes } from 'features/CustomCampaigns/helpers'
import moment from 'moment-timezone'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router'
import { useToasts } from 'react-toast-notifications'
import announcementApi from 'services/api/announcement'

import styles from './Announcements.module.scss'
import Sidebar from './Sidebar'

const Announcements = () => {
  const { audience } = useParams()
  const timeZoneId = useSelector(s => s.hotelDetails.ianaTimeZoneId)
  const { addToast } = useToasts()
  const { promptEngagePlusUpgrade, promptEngagePlusUpgradeOnEntry } = useEngagePlusGate()

  const [showingDialog, setShowingDialog] = useState(false)
  const [announcementType, setAnnouncementType] = useState()
  const [isLoading, setIsLoading] = useState(true)
  const [messages, setMessages] = useState([])
  const [trimmedMessages, setTrimmedMessages] = useState([])
  const [isLoadingMessages, setIsLoadingMessages] = useState(false)
  const [audienceSizes, setAudienceSizes] = useState()
  const [committedRemaining, setCommittedRemaining] = useState()
  const [newMessageOverWrites, setNewMessageOverWrites] = useState([])
  const [defaultText, setDefaultText] = useState('')
  const [checkIndex, setCheckIndex] = useState(0)
  const [lastChecked, setLastChecked] = useState()
  const [showScrollToBottom, setShowScrollToBottom] = useState(false)
  const [announcementIdBeingEdited, setAnnouncementIdBeingEdited] = useState()

  const [hasMoreMessagesToShow, setHasMoreMessagesToShow] = useState(false)

  const [messageLoadingLimit, setMessageLoadingLimit] = useState(100)

  const [previousContainerHeight, setPreviousContainerHeight] = useState()

  const containerRef = useRef(null)
  const bubblesContainerRef = useRef(null)
  const footerRef = useRef(null)

  useEffect(() => {
    setNewMessageOverWrites([])
  }, [audience])

  useEffect(() => {
    document.querySelector('body').classList.add('darkBody')
    return () => {
      document.querySelector('body').classList.remove('darkBody')
    }
  }, [])

  useEffect(() => {
    promptEngagePlusUpgradeOnEntry()
  }, [promptEngagePlusUpgradeOnEntry])

  const fetchAudienceCapacity = useCallback(() => {
    return announcementApi.getAudiences().then(data => {
      setAudienceSizes(data.guests)
      if (data.committedRemaining != null) {
        setCommittedRemaining(data.committedRemaining)
      }
      return data
    })
  }, [])

  useEffect(() => {
    if (audience) {
      setIsLoading(true)
      fetchAudienceCapacity().finally(() => {
        setIsLoading(false)
      })
    }
  }, [audience, fetchAudienceCapacity])

  useEffect(() => {
    announcementApi.getDefaultText({ audience }).then(data => {
      setDefaultText(data.defaultTextContent)
    })
  }, [audience])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container
      setShowScrollToBottom(scrollHeight - scrollTop - clientHeight > 100)
    }

    container.addEventListener('scroll', handleScroll)
    return () => container.removeEventListener('scroll', handleScroll)
  }, [])

  const scrollToBottom = useCallback(() => {
    if (footerRef.current) {
      footerRef.current.scrollIntoView()
    }
  }, [])

  useEffect(() => {
    if (messages.length > 0 && !isLoadingMessages) {
      setTimeout(() => {
        scrollToBottom()
      }, 10)
    }
  }, [messages, isLoadingMessages, scrollToBottom])

  const fetchMessages = useCallback(() => {
    setIsLoadingMessages(true)
    setMessages([])
    announcementApi
      .getMessages(audience)
      .then(data => {
        setMessages([...data.items])
      })
      .catch(() => {
        addToast(`Couldn't get earlier notifications`, { appearance: 'error' })
      })
      .finally(() => {
        setIsLoadingMessages(false)
        setLastChecked(new Date().toISOString())
        scrollToBottom()
      })
  }, [audience, addToast, scrollToBottom])

  useEffect(() => {
    let optimizedMessages = messages.slice(-messageLoadingLimit)

    setHasMoreMessagesToShow(messages.length > optimizedMessages.length)

    setTrimmedMessages(optimizedMessages)

    // maintain scroll position when loading more messages
    setTimeout(() => {
      if (previousContainerHeight < bubblesContainerRef.current?.scrollHeight) {
        bubblesContainerRef.current.scrollTop =
          bubblesContainerRef.current.scrollHeight - previousContainerHeight
      }
    }, 0)
  }, [messages, messageLoadingLimit, previousContainerHeight])

  useEffect(() => {
    if (audience) {
      fetchMessages()
    }
  }, [audience, checkIndex, fetchMessages])

  useEffect(() => {
    function updateResults() {
      announcementApi
        .getMessages(audience)
        .then(data => {
          setMessages([...data.items])
        })
        .catch(() => {
          addToast(`Couldn't fetch announcements`, { appearance: 'error' })
        })
        .finally(() => {
          setLastChecked(new Date().toISOString())
        })
    }

    if (
      messages.find(a => a.customDeliveryStatus === customCampaignStatusTypes.Approved)
    ) {
      const scheduledUpdate = setTimeout(updateResults, 30000)
      return () => clearTimeout(scheduledUpdate)
    }
  }, [messages, audience, addToast])

  const handleClickSchedule = () => {
    if (promptEngagePlusUpgrade()) {
      return
    }

    setAnnouncementType('schedule')
    setShowingDialog(true)
  }

  const handleClickMessageNow = () => {
    if (promptEngagePlusUpgrade()) {
      return
    }

    setAnnouncementType('immediate')
    setShowingDialog(true)
  }

  const handleClickEdit = recurringAnnouncementId => {
    if (promptEngagePlusUpgrade()) {
      return
    }

    setAnnouncementIdBeingEdited(recurringAnnouncementId)
    setShowingDialog(true)
  }

  const handleCloseEditDialog = () => {
    setShowingDialog(false)
    setAnnouncementIdBeingEdited()
  }

  const handleMessageSent = overwrites => {
    setNewMessageOverWrites(overwrites)
    fetchMessages()
  }

  function enableRecurringAnnouncement(recurringAnnouncementId) {
    if (promptEngagePlusUpgrade()) {
      return
    }

    announcementApi.enableRecurringAnnouncement(recurringAnnouncementId).then(() => {
      setCheckIndex(i => i + 1)
    })
  }

  function disableRecurringAnnouncement(recurringAnnouncementId) {
    if (promptEngagePlusUpgrade()) {
      return
    }

    announcementApi.disableRecurringAnnouncement(recurringAnnouncementId).then(() => {
      setCheckIndex(i => i + 1)
    })
  }

  function cancelRecurringAnnouncement(recurringAnnouncementId) {
    if (promptEngagePlusUpgrade()) {
      return
    }

    announcementApi.cancelRecurringAnnouncement(recurringAnnouncementId).then(() => {
      setCheckIndex(i => i + 1)
    })
  }

  return (
    <div className={styles.wrapper}>
      <CustomHeader title="Announcements" width={1300} />
      <Sidebar />
      <div className={styles.chatContainer} ref={containerRef}>
        {!audience && <NoMessages>Select a guest category to message</NoMessages>}
        {audience && (
          <>
            <div className={styles.topBar}>
              <AudienceInfoBar
                audience={audience}
                audienceSize={audienceSizes?.[audience]?.total}
                totalEligibleForText={
                  audienceSizes?.[audience]?.totalEligiblePhoneNumberCount
                }
              />
              <SendMessageBar
                audience={audience}
                onClickSchedule={handleClickSchedule}
                onClickMessageNow={handleClickMessageNow}
                canSendMessageNow={
                  audienceSizes?.[audience]?.totalEligiblePhoneNumberCount > 0
                }
              />
            </div>
            {trimmedMessages.length > 0 && (
              <div className={styles.messagesHolder}>
                <div className={styles.bubblesContainer} ref={bubblesContainerRef}>
                  {hasMoreMessagesToShow && (
                    <button
                      className={styles.showEarlierMessagesButton}
                      onClick={() => {
                        setMessageLoadingLimit(m => m + 100)
                        setPreviousContainerHeight(
                          bubblesContainerRef.current?.scrollHeight
                        )
                      }}
                    >
                      Show earlier messages
                    </button>
                  )}
                  {trimmedMessages
                    .filter(
                      m =>
                        m.recurringAnnouncementId
                          ? true
                          : !(
                              m.customDeliveryStatus === customCampaignStatusTypes.Deleted
                            ) // filter out deleted single announcements
                    )
                    .sort(
                      (a, b) =>
                        new Date(
                          a.deliveryFinishDate || a.deliveryStartDate || a.addedOn
                        ) -
                        new Date(b.deliveryFinishDate || b.deliveryStartDate || b.addedOn)
                    )
                    .reduce(
                      (pm, m, i, arr) => [
                        ...pm,
                        // <FutureDivider
                        //   key={'f' + m.contentId}
                        //   prevMessage={arr[i - 1] || null}
                        //   currentMessage={m}
                        // />,
                        <DateDivider
                          key={'d' + m.contentId}
                          prevMessage={arr[i - 1] || null}
                          currentMessage={m}
                        />,
                        <ConversationBubble
                          key={m.contentId}
                          messageTemplate={m.messageTemplate}
                          messageMediaUrls={m.messageMediaUrls}
                          deliveryRemaining={m.deliveryRemaining}
                          deliverySize={m.deliverySize}
                          addedBy={m.addedBy}
                          audience={audience}
                          contentId={m.contentId}
                          deliveryStartDate={m.deliveryStartDate}
                          deliveryFinishDate={m.deliveryFinishDate}
                          addedOn={m.addedOn}
                          isActive={m.isActive}
                          customDeliveryStatus={m.customDeliveryStatus}
                          isNewMessage={newMessageOverWrites.find(
                            o => o.contentId === m.contentId
                          )}
                          {...newMessageOverWrites.find(o => o.contentId === m.contentId)}
                          recurringAnnouncementId={m.recurringAnnouncementId}
                          onClickEdit={() => handleClickEdit(m.recurringAnnouncementId)}
                          enableAnnouncement={() => {
                            enableRecurringAnnouncement(m.recurringAnnouncementId)
                          }}
                          disableAnnouncement={() => {
                            disableRecurringAnnouncement(m.recurringAnnouncementId)
                            setCheckIndex(i => i + 1)
                          }}
                          cancelAnnouncement={() => {
                            cancelRecurringAnnouncement(m.recurringAnnouncementId)
                            setCheckIndex(i => i + 1)
                          }}
                          refreshResults={() => setCheckIndex(i => i + 1)}
                          repeatKind={m.repeatKind}
                          daysOfWeek={m.daysOfWeek}
                        />
                      ],
                      []
                    )}
                  <div ref={footerRef}></div>
                </div>
                <div className={styles.lastUpdatedBar}>
                  <button onClick={() => setCheckIndex(i => i + 1)}>
                    <RefreshIcon />
                  </button>
                  <span>
                    Last updated on {moment(lastChecked).tz(timeZoneId).format('h:mm A')}{' '}
                    (hotel time)
                  </span>
                </div>
              </div>
            )}
            {messages.length === 0 && !isLoadingMessages && (
              <div className={styles.emptyMessagesHolder}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    height: '100%',
                    fontSize: 17,
                    color: '#8a8e92',
                    flexDirection: 'column'
                  }}
                >
                  <AnnouncementIcon
                    fill="#A8ADB2"
                    style={{ fontSize: 50, marginBottom: 10, marginTop: -20 }}
                  />
                  You'll see the past announcements here.
                </div>
              </div>
            )}
            {isLoadingMessages && (
              <div className={styles.emptyMessagesHolder}>
                <LoadingIndicator />
              </div>
            )}
            {showingDialog && !isLoading && (
              <EditTextDialog
                onClose={handleCloseEditDialog}
                onMessageSent={handleMessageSent}
                announcementType={announcementType}
                audience={audience}
                totalEligibleForText={
                  audienceSizes?.[audience]?.totalEligiblePhoneNumberCount
                }
                committedRemaining={committedRemaining}
                onScheduledCountUpdated={setCommittedRemaining}
                defaultText={defaultText}
                announcementIdBeingEdited={announcementIdBeingEdited}
                refreshResults={() => {
                  setCheckIndex(i => i + 1)
                  fetchAudienceCapacity()
                }}
              />
            )}
          </>
        )}
        {showScrollToBottom && (
          <button className={styles.scrollToBottomButton} onClick={scrollToBottom}>
            Scroll to Latest Messages
          </button>
        )}
      </div>
    </div>
  )
}

export default Announcements
